import React, { useState, useEffect, useRef, useContext } from 'react';
import { Modal, Input, Row, Col, Button, Typography } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import openNotificationWithIcon from '../Notification';
import { PMS_APIs, PMS_ENDPOINT, SITE_KEY } from 'src/configs/API';
import { GlobalContext } from 'src/pages/_app';
import { useRouter } from 'next/router';

const { Text } = Typography;

const CODE_LENGTH = 6;
const RESEND_TIME = 180;

const OtpInputs = React.memo(function OtpInputs({
  code,
  inputRefs,
  handleChange,
  handleKeyDown,
  handlePaste,
}) {
  return (
    <Row gutter={12} justify="center" style={{ marginBottom: 16 }}>
      {code.map((digit, idx) => (
        <Col key={idx} span={3}>
          <Input
            maxLength={1}
            value={digit}
            onChange={e => handleChange(e.target.value, idx)}
            onKeyDown={e => handleKeyDown(e, idx)}
            onPaste={handlePaste}
            ref={ref => (inputRefs.current[idx] = ref)}
            style={{
              height: 50,
              width: 50,
              textAlign: 'center',
              fontSize: 20,
              borderRadius: 8
            }}
          />
        </Col>
      ))}
    </Row>
  );
});


const ResendCounter = React.memo(function ResendCounter({ counter, onResend }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: 20 }}>
      {counter > 0 ? (
        <Text type="secondary">Gửi lại sau {counter}s</Text>
      ) : (
        <Button type="link" onClick={onResend} style={{ padding: 0 }}>
          Gửi lại mã
        </Button>
      )}
    </div>
  );
});

const VerifyLoginModal = ({ open, onClose, token, message, tFAMethod }) => {
  const router = useRouter();
  const { getUser } = useContext(GlobalContext);

  const inputRefs = useRef([]);
  const recaptchaRef = useRef(null);
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(''));
  const [counter, setCounter] = useState(RESEND_TIME);
  const [tokenRecaptcha, setTokenRecaptcha] = useState('');

 
useEffect(() => {
    if (!open) return;

    setCode(Array(CODE_LENGTH).fill(''));
    setCounter(RESEND_TIME);

    const timer = setInterval(() => {
      setCounter(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open]);

  const handleChange = (value, index) => {
    if (!/^[0-9]*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const resetRecaptcha = () => {
    recaptchaRef.current?.reset?.();
    setTokenRecaptcha('');
  };

  const requireRecaptcha = () => {
    if (typeof window !== 'undefined' && !window.location.hostname.includes('localhost')) {
      if (!tokenRecaptcha) {
        resetRecaptcha();
        openNotificationWithIcon('error', 'Hệ thống', 'Vui lòng xác thực mã captcha');
        return false;
      }
    }
    return true;
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text') || '';
    const digits = paste.replace(/[^0-9]/g, '').slice(0, CODE_LENGTH).split('');
    if (!digits.length) return;

    const newCode = Array(CODE_LENGTH).fill('');
    digits.forEach((d, i) => (newCode[i] = d));
    setCode(newCode);

    const firstEmpty = newCode.findIndex(c => c === '');
    if (firstEmpty === -1) {
      inputRefs.current[CODE_LENGTH - 1]?.focus();
    } else {
      inputRefs.current[firstEmpty]?.focus();
    }
  };


  const handleSubmit = () => {
    const finalCode = code.join('');
    if (finalCode.length === CODE_LENGTH && requireRecaptcha()) {
      handleVerify(finalCode);
    }
  };

  const handleVerify = async (finalCode) => {
    try {
      const res = await axios.post(PMS_ENDPOINT + PMS_APIs.API_TFE_VERIFY_LOGIN_TOKEN, {
        otpToken: token,
        otp: finalCode
      }, {
        headers: {
          'TokenRecaptcha': tokenRecaptcha
        }
      });

      if (res.data?.isError) {
        openNotificationWithIcon('error', 'Hệ thống', res.data?.errorMessage);
        return;
      }

      getUser(res.data);
      openNotificationWithIcon('success', 'Hệ thống', 'Xác thực thành công');
      window.localStorage.setItem('user', JSON.stringify(res.data));

      setTimeout(() => {
        router.push('/khach-hang/tao-don-hang');
      }, 1000);

      onClose();
    } catch (err) {
      console.error(err);
      openNotificationWithIcon('error', 'Hệ thống', 'Có lỗi xảy ra');
    }
  };


  const onResend = () => {
    axios.post(PMS_ENDPOINT + PMS_APIs.API_TFE_RESEND_LOGIN_OTP, {
      OtpToken: token
    }).then(res => {
      if (res?.data?.isOtpDetroyed === true) {
        router.reload();
      } else {
        setCounter(RESEND_TIME);
        const timer = setInterval(() => {
          setCounter(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
      title={
        <div style={{ textAlign: 'center', fontSize: 18, fontWeight: 600 }}>
          Nhập mã xác nhận
        </div>
      }
    >
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <Text>{message || 'Mã xác nhận đã được gửi đến email đã đăng ký'}</Text>
      </div>

      <OtpInputs
        code={code}
        inputRefs={inputRefs}
        handleChange={handleChange}
        handleKeyDown={handleKeyDown}
        handlePaste={handlePaste}
      />
      {tFAMethod === 'EMAIL' && (
        <ResendCounter counter={counter} onResend={onResend} />
      )}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={SITE_KEY}
          theme="light"
          onChange={(token) => setTokenRecaptcha(token)}
          onExpired={() => setTokenRecaptcha('')}
          hl="vi"
        />
      </div>
      <Button
        type="primary"
        block
        style={{ height: 44 }}
        onClick={handleSubmit}
        disabled={code.some(c => c === '')}
        htmlType="button"
      >
        Xác nhận
      </Button>
    </Modal>
  );
};

export default VerifyLoginModal;
